"use strict";
/**
 * The NBA tap recorder (ai/nba/N8-taps/PLAN.md). Pure: markup and taps in, out.
 *
 * Measurement only, and separate from trading: no order control lives here. Landscape, two
 * thumbs (§11.1): away on the left and home on the right as seen from most seats on TV, the half
 * tapped is the side; each half two rows of three; PULL QUOTES and UNDO in the centre strip.
 * Buttons fire on pointerdown (handlers/nba-tap-handler.ts), so nothing here may reflow when a
 * tap lands: each FT area keeps fixed cells (a b / c q), FT ? always in q, and swaps only which
 * buttons fill a, b and c: the trip starts, the trip's attempts, or a technical's (§12.6).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NBA_TAP_LABELS = void 0;
exports.nbaFtButtonMode = nbaFtButtonMode;
exports.nbaFtAreas = nbaFtAreas;
exports.renderNbaTapPad = renderNbaTapPad;
exports.renderNbaLastTap = renderNbaLastTap;
exports.buildNbaTap = buildNbaTap;
exports.nbaUndoTarget = nbaUndoTarget;
exports.renderNbaFtState = renderNbaFtState;
exports.renderNbaTapStatus = renderNbaTapStatus;
exports.renderNbaTrack = renderNbaTrack;
exports.nbaTrackingLeftOn = nbaTrackingLeftOn;
const tap_vocabulary_1 = require("../../shared/tap-vocabulary");
const tap_parked_1 = require("./tap-parked");
const FT_TRIP = [
    { kind: "ft_trip_1", label: "FT ×1" },
    { kind: "ft_trip_2", label: "FT ×2" },
    { kind: "ft_trip_3", label: "FT ×3" },
    { kind: "ft_unsure", label: "FT ?" },
];
// Field goals and Def reb above the trip block, the trip's attempts below it (A178).
const ABOVE_TRIP = [
    { kind: "made_2", label: "Made 2" },
    { kind: "made_3", label: "Made 3" },
    { kind: "def_rebound", label: "Def reb" },
];
const BELOW_TRIP = [
    { kind: "ft_made", label: "FT made" },
    { kind: "ft_missed", label: "FT miss" },
    { kind: "turnover", label: "Turnover" },
];
// A technical's one awarded attempt and its explicit result (§12.6, A198).
const TECH = [
    { kind: "ft_tech", label: "Tech FT" },
    { kind: "ft_tech_made", label: "Tech made" },
    { kind: "ft_tech_missed", label: "Tech missed" },
];
const SIDED = [...ABOVE_TRIP, ...FT_TRIP, ...BELOW_TRIP, ...TECH];
const ATTEMPT_KINDS = ["ft_made", "ft_missed"];
const TECH_ATTEMPT_KINDS = ["ft_tech_made", "ft_tech_missed"];
/** Every attempt starts off: the pad opens with nothing pending. */
const STARTS_OFF = [...ATTEMPT_KINDS, ...TECH_ATTEMPT_KINDS];
exports.NBA_TAP_LABELS = {
    ...Object.fromEntries(SIDED.map((s) => [s.kind, s.label])),
    pull: "PULL QUOTES",
    undo: "UNDO",
    session_start: "pad opened",
    track_start: "tracking started",
    track_stop: "tracking stopped",
};
function escapeHtml(value) {
    return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}
function button(kind, cls, side = null) {
    const sideAttr = side ? ` data-side="${side}"` : "";
    const off = STARTS_OFF.includes(kind) ? " disabled" : "";
    return `<button type="button" class="nba-tap-btn ${cls}" data-tap-kind="${kind}"${sideAttr}${off}>${escapeHtml(exports.NBA_TAP_LABELS[kind])}</button>`;
}
/** The mode whose buttons a swapping FT button belongs to. */
function nbaFtButtonMode(kind) {
    return ATTEMPT_KINDS.includes(kind) ? "attempt" : TECH_ATTEMPT_KINDS.includes(kind) ? "tech" : "trip";
}
/**
 * Each side's FT area for a step (A178, §12.6): a pending technical's side shows its result
 * buttons; the other side's open trip stays in attempt mode, marked held, its buttons live; with
 * no technical, the open trip's side shows its attempts. Everything else shows the trip starts.
 */
function nbaFtAreas(step) {
    const { trip, tech } = step;
    const area = (side) => {
        if (tech?.side === side)
            return { mode: "tech", held: false, progress: tech.shots > 1 ? `tech ×${tech.shots}` : "tech" };
        if (trip.kind === "open" && trip.side === side) {
            const count = `${trip.attempt} of ${trip.of}`;
            return tech ? { mode: "attempt", held: true, progress: `held · ${count}` } : { mode: "attempt", held: false, progress: count };
        }
        return { mode: "trip", held: false, progress: "" };
    };
    return { home: area("home"), away: area("away") };
}
/** An FT area button in a fixed cell; `swaps` marks the ones the area's mode shows or hides. */
function ftButton(kind, side, cell, swaps, label) {
    const off = STARTS_OFF.includes(kind) ? " disabled" : "";
    const group = kind === "ft_unsure" ? "" : ` nba-ft-${nbaFtButtonMode(kind) === "trip" ? "trip-btn" : nbaFtButtonMode(kind) === "tech" ? "tech-attempt" : "attempt"}`;
    const cls = `nba-tap-btn nba-ft-btn${swaps ? " nba-ft-swaps" : ""}${group}`;
    return `<button type="button" class="${cls}" data-tap-kind="${kind}" data-side="${side}" data-ft-cell="${cell}"${off}>${escapeHtml(label ?? exports.NBA_TAP_LABELS[kind])}</button>`;
}
function half(side, team) {
    return `<section class="nba-half nba-half-${side}" data-side="${side}">
			<div class="nba-half-team">${side === "home" ? "HOME" : "AWAY"} · ${escapeHtml(team)}</div>
			<div class="nba-half-grid">
				${button("made_2", "nba-kind-score", side)}${button("made_3", "nba-kind-score", side)}${button("def_rebound", "nba-kind-poss", side)}
				<div class="nba-poss-split">${button("turnover", "nba-kind-poss", side)}${button("ft_tech", "nba-kind-tech", side)}</div>
				<div class="nba-ft-area" data-side="${side}" data-mode="trip">
					${ftButton("ft_trip_1", side, "a", true)}${ftButton("ft_trip_2", side, "b", true)}${ftButton("ft_trip_3", side, "c", true)}
					${ftButton("ft_made", side, "a", true)}${ftButton("ft_missed", side, "b", true)}
					${ftButton("ft_tech_made", side, "a", true)}${ftButton("ft_tech_missed", side, "b", true)}
					<span class="nba-ft-progress" data-ft-cell="c" aria-live="polite"></span>
					${ftButton("ft_unsure", side, "q", false)}
				</div>
			</div>
		</section>`;
}
function renderNbaTapPad(teams, title) {
    return `<div class="tap-pad nba-tap-pad nba-pad-v2">
		<div class="nba-pad-head">
			<button type="button" class="nba-tap-exit btn btn-small">Exit</button>
			<span class="tap-pad-title">${escapeHtml(title)}</span>
			${tap_parked_1.PARKED_HTML /* ahead of the fixed-width track controls, so a long track note cannot push it off a 667 px screen.
        The one-time shift when the first tap parks is accepted: reserving its space would squeeze the status at 667 px. */}
			<button type="button" class="nba-track btn btn-small" disabled>Start tracking (waiting for clock)</button>
			<span class="nba-track-note" role="status"></span>
			<span class="tap-pad-status nba-tap-status" role="status"></span>
			<span class="nba-last"></span>
			<div class="nba-ft-state" role="status" aria-live="polite">${renderNbaFtState({ trip: { kind: "unknown" }, tech: null }, teams)}</div>
		</div>
		<div class="nba-portrait-hint">Turn the phone to landscape</div>
		<div class="nba-halves">
			${half("away", teams.away)}
			<div class="nba-centre">${button("pull", "tap-pull")}${button("undo", "tap-undo")}</div>
			${half("home", teams.home)}
		</div>
	</div>`;
}
/** The header's one line: the latest tap of any session, e.g. "last 20:41:15 Made 3 BOS". */
function renderNbaLastTap(items, teams) {
    const t = [...items].reverse().find((x) => x.kind !== "session_start" && !tap_vocabulary_1.NBA_TRACK_KINDS.has(x.kind));
    if (!t)
        return "";
    const at = new Date(t.atMs).toLocaleTimeString(undefined, { hour12: false });
    const team = t.side ? ` ${t.side === "home" ? teams.home : teams.away}` : "";
    const label = exports.NBA_TAP_LABELS[t.kind] ?? t.kind;
    return escapeHtml(`last ${at} ${label}${team}`);
}
/** One tap, built at the touch: its own time, and the clock offset in force with its age. */
function buildNbaTap(a) {
    const sided = a.kind !== "pull" && a.kind !== "undo" && a.kind !== "session_start" && !tap_vocabulary_1.NBA_TRACK_KINDS.has(a.kind);
    return {
        tapId: a.tapId,
        kind: a.kind,
        side: sided ? a.side : null,
        undoesTapId: a.undoesTapId,
        value: null,
        playSeq: null,
        clientTs: new Date(a.atMs).toISOString(),
        clientOffsetMs: a.clock?.offsetMs ?? null,
        clientRttMs: a.clock?.rttMs ?? null,
        clientSyncAgeMs: a.clock ? Math.max(0, a.nowMs - a.clock.at) : null,
        sendAttempts: 0, // the queue counts each attempt
        sessionId: a.sessionId,
    };
}
/**
 * UNDO's target: this session's latest tap that is neither an UNDO, nor the session start, nor
 * already undone. Never another session's (N8 §10.1: that UNDO would be invalid).
 */
function nbaUndoTarget(history, sessionId) {
    const undone = new Set(history.filter((t) => t.kind === "undo").map((t) => t.undoesTapId));
    for (let i = history.length - 1; i >= 0; i--) {
        const t = history[i];
        if (t.sessionId !== sessionId)
            return null;
        if (t.kind !== "undo" && t.kind !== "session_start" && !tap_vocabulary_1.NBA_TRACK_KINDS.has(t.kind) && !undone.has(t.tapId))
            return t;
    }
    return null;
}
/**
 * The pad's display state (this session's taps, in tap order); unknown is withheld, never zero.
 * A pending technical comes first, with its count, then what follows it (§12.6).
 */
function renderNbaFtState(step, teams) {
    const team = (side) => (side === "home" ? teams.home : teams.away);
    const { trip, tech } = step;
    const tripText = trip.kind === "open" ? `${trip.attempt} of ${trip.of} · ${team(trip.side)}` : trip.kind;
    if (!tech)
        return escapeHtml(`FT: ${tripText}`);
    const techText = `tech${tech.shots > 1 ? ` ×${tech.shots}` : ""} · ${team(tech.side)}`;
    return escapeHtml(trip.kind === "none" ? `FT: ${techText}` : `FT: ${techText}, then ${tripText}`);
}
function renderNbaTapStatus(s) {
    const parts = s.storageFailing ? ["taps not saved on phone"] : [];
    parts.push(s.pending === 0 ? "all sent" : `${s.pending} unsent`);
    if (s.offsetMs === null)
        parts.push("clock not synced");
    else
        parts.push(`clock ${s.offsetMs >= 0 ? "+" : ""}${Math.round(s.offsetMs)} ms ±${Math.round((s.rttMs ?? 0) / 2)} (${s.syncedAgoS ?? 0}s ago)`);
    if (!s.screenAwake)
        parts.push("screen may sleep");
    if (s.failedSends)
        parts.push(`${s.failedSends} failed sends`);
    if (s.conflicts)
        parts.push(`${s.conflicts} refused`);
    if (s.error)
        parts.push(`retrying: ${s.error}`);
    return escapeHtml(parts.join(" · "));
}
/**
 * The tracking control (N8 §16a): disabled until the session in force is synchronized; a new
 * session never resumes tracking, and says so when this game's latest tracking press was a start.
 */
function renderNbaTrack(a) {
    const at = (ms) => new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    if (a.sinceMs !== null)
        return { label: `● TRACKING ${at(a.sinceMs)} · Stop`, on: true, disabled: false, note: "" };
    const note = a.leftOnSinceMs !== null ? `Not tracking: was on before reload (${at(a.leftOnSinceMs)})` : "";
    return a.synced
        ? { label: "Start tracking", on: false, disabled: false, note }
        : { label: "Start tracking (waiting for clock)", on: false, disabled: true, note };
}
/** The press time of this game's latest tracking start, if no stop followed it (any session). */
function nbaTrackingLeftOn(history) {
    const last = [...history].filter((t) => tap_vocabulary_1.NBA_TRACK_KINDS.has(t.kind)).sort((x, y) => x.atMs - y.atMs).pop();
    return last?.kind === "track_start" ? last.atMs : null;
}
